import React, { useState } from "react";
import ResizableCell from "../../Utility/ResizableCell";
import { Link, useNavigate, useParams } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { PlusCircleFill } from "react-bootstrap-icons";
import useDownloader from "react-use-downloader";
import { toast } from "react-toastify";
import { getUrlFromS3 } from "../api";
import Modal from "react-bootstrap/Modal";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "../../../App";
import { updateLaborReport } from "../api";
import { useGetAllCategories } from "../../Classification/api";
import { findTitleById } from "../../Classification/utils";
import CustomTreeSelect from "../../Common/CustomTreeSelect";
export default function LabourTable({
  reports,
  setReports,
  allLaborClasses,
  handleUpdateRawLabor,
  handleAddNewLaborClass,
  fetchAgain = false,
  getUnassignedLaborReport,
}) {
  const { id } = useParams();
  const { download, isInProgress } = useDownloader();
  const navigate = useNavigate();
  const [editableCell, setEditableCell] = useState(null);
  const [newLaborClass, setNewLaborClass] = useState("");
  const [newLaborCategory, setNewLaborCategory] = useState(null);
  const [newLaborPosId, setNewLaborPosId] = useState("");
  const [show, setShow] = useState(false);

  const { data: allCategories, isLoading } = useGetAllCategories({
    restaurant_id: id,
  });

  const { mutate, isLoading: addingMapper } = useMutation({
    mutationFn: updateLaborReport,
    onSuccess: (data) => {
      toast.success(data.message);
    },
    onError: () => {
      toast.error("Error updating labor report");
    },
    onSettled: () => {
      queryClient.invalidateQueries();
      if (fetchAgain) {
        getUnassignedLaborReport();
      }
    },
  });

  if (!reports || reports.length === 0) {
    return <p>No reports available</p>;
  }
  const oheaders = Object.keys(reports[0]).filter(
    (key) => key !== "_id" && key !== "report_id"
  );
  const desiredOrder = [
    "employee_id",
    "fname",
    "lname",
    "employee_type",
    "labor_category",
    "labor_category_confidence",
    "hr_wage",
    "hrs_recorded",
    "in_time",
    "out_time",
    "date",
  ];

  // Filter the headers based on the desired order
  const headers = desiredOrder.filter((header) => oheaders.includes(header));

  const downloadHandler = async (restaurant_id, report_date, report_type) => {
    try {
      const response = await getUrlFromS3(
        restaurant_id,
        report_date,
        report_type
      );
      if (response?.file_url === undefined) {
        toast.error("File not available");
        return;
      }
      download(response.file_url, response.file_name);
    } catch (error) {
      toast.error("File not available");
    }
  };

  return (
    <div style={{ overflowX: "auto", height: "650px" }}>
      <table className="table table-striped table-hover table-bordered shadow-sm table-responsive table-stack">
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                style={{
                  textAlign: "center",
                  textTransform: "capitalize",
                }}
              >
                <ResizableCell>{header.replace(/_/g, " ")}</ResizableCell>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reports.map((row) => (
            <React.Fragment key={row.item_uuid}>
              <tr>
                {headers.map((header) => (
                  <td
                    data-label={header.replace(/_/g, " ")}
                    style={{
                      textAlign: "center",
                    }}
                    key={header}
                    onClick={() => {
                      setEditableCell(row._id);
                    }}
                  >
                    {editableCell === row._id && header == "employee_type" ? (
                      <Dropdown>
                        <Dropdown.Toggle variant="success" id="dropdown-basic">
                          {row[header] ? row[header] : "Select"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          style={{ maxHeight: "200px", overflowY: "scroll" }}
                        >
                          <div className="d-flex mx-2 justify-content-between">
                            <p>Add a New Labor Class</p>
                            <button
                              className="btn btn-success mx-1"
                              onClick={() => {
                                setNewLaborPosId(row.pos_id);
                                setShow(true);
                              }}
                            >
                              <PlusCircleFill className="my-1" />
                            </button>
                          </div>
                          {allLaborClasses.map((laborClass) => (
                            <Dropdown.Item
                              key={laborClass.labor_id}
                              onClick={() => {
                                handleUpdateRawLabor(
                                  row._id,
                                  laborClass.labor_id
                                );
                                setEditableCell(null);
                              }}
                              style={{
                                textTransform: "capitalize",
                              }}
                            >
                              {laborClass["Job Title"]}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : header == "hr_wage" && editableCell === row._id ? (
                      <input
                        className="form-control"
                        type="text"
                        value={row[header]}
                        onChange={(e) => {
                          const newWage = e.target.value;
                          setReports((prevData) =>
                            prevData.map((report) =>
                              report._id === row._id
                                ? { ...report, hr_wage: newWage }
                                : report
                            )
                          );
                        }}
                        onBlur={() => {
                          setEditableCell(null);
                          mutate(row);
                        }}
                      />
                    ) : header == "employee_id" ? (
                      <Link
                        to={`/restaurants/reports/report-details/${row.report_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-body"
                        style={{ textDecoration: "underline" }}
                      >
                        {row[header]}
                      </Link>
                    ) : header === "labor_category" ? (
                      <>
                        {allCategories && !isLoading
                          ? findTitleById(
                              allCategories?.categories.filter(
                                (category) => category.type == "Labor"
                              ),
                              row[header]
                            ) 
                          : row[header]}
                      </>
                    ) : typeof row[header] === "boolean" ? (
                      row[header] ? (
                        "Yes"
                      ) : (
                        "No"
                      )
                    ) : (
                      row[header]
                    )}
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="sm"
        autoFocus={true}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Labor Class</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control my-2 mx-1"
            placeholder="Enter new labor class"
            value={newLaborClass}
            onChange={(e) => setNewLaborClass(e.target.value)}
          />
          <CustomTreeSelect
            data={allCategories?.categories.filter(
              (category) => category.type === "Labor" &&
              !category.is_manual
            )}
            onChange={(value) => {
              console.log("the value here is ", value);
              setNewLaborCategory(value.value);
            }}
            value={findTitleById(
              allCategories?.categories.filter(
                (category) => category.type === "Labor" &&
                !category.is_manual
              ),
              newLaborCategory
            )}
            placeholder="Select Labor Category"
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-success"
            onClick={() => {
              handleAddNewLaborClass(
                newLaborPosId,
                newLaborClass,
                newLaborCategory
              );
              setNewLaborClass("");
              setNewLaborCategory("");
              setNewLaborPosId("");
              setShow(false);
            }}
          >
            Add
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
